// =====================================================================
// The scroll engine — one requestAnimationFrame loop for the whole menu.
//
// Each frame: read scrollTop, header height and the rects of every
// scroll-linked element ONCE, then write CSS custom properties and inline
// transforms directly. React state changes only on discrete events
// (header tone flips, active tab, chapter opener "on", current section).
// Port of the prototype's loop / scan / els (Salesol Menu.dc.html 545–592).
// =====================================================================
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { SECTION_TAB } from './data/adapt.js';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

// Haptics only after the first tap: browsers block (and log) vibrate() before
// any user gesture, and a deep-link landing scrolls before one.
let touched = false;
if (typeof window !== 'undefined') window.addEventListener('pointerdown', () => { touched = true; }, { once: true, passive: true });
export function buzz(ms) {
  if (!touched) return;
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_) { /* ignore */ }
}

/**
 * @param {object} o
 * @param {React.RefObject<HTMLElement>} o.frameRef   the phone frame (tilt vars are written here)
 * @param {React.RefObject<HTMLElement>} o.appRef     the scroll container [data-app]
 * @param {boolean} o.rm                                prefers-reduced-motion
 * @param {{tilt:React.RefObject, tiltT:React.RefObject, ptr:React.RefObject}} o.tiltSrc
 * @param {(ui:{night:boolean, tab:string, hdBg:string|null}) => void} o.onUi   called only when the tone, the active tab or the header's page colour changes
 * @param {React.RefObject<object>} o.glRef             GlStage instance (may be null until three loads)
 */
export default function useScrollEngine({ frameRef, appRef, rm, tiltSrc, onUi, glRef }) {
  const ref = useRef(null);
  const onUiRef = useRef(onUi);
  onUiRef.current = onUi;

  const engine = useMemo(() => {
    const E = {
      rm,
      onAt: {},           // { beans: t, table: t } — when each opener switched on
      openState: {},      // per-opener flags that must survive els() rebuilds
      detailGL: false,    // true while the origin globe owns the canvas
      _els: null,
      _lastSt: -1,
      _pr: -1,
      _hd: -1,
      _vh: -1,
      _f: 0,
      night: true,
      hdBg: null,
      tab: 'sig',
      section: 's-top',
      _subs: new Set(),
      _alive: false,

      app() { return appRef.current; },
      frame() { return frameRef.current; },
      invalidate() { E._els = null; },
      onSection(cb) { E._subs.add(cb); return () => E._subs.delete(cb); },
      hdH() { const hd = E.app()?.querySelector('[data-hd]'); return hd ? hd.offsetHeight : 100; },
      scrollToY(y, behavior) {
        const app = E.app(); if (!app) return;
        E.lastGoY = y;
        app.scrollTo({ top: y, behavior: behavior || (E.rm ? 'auto' : 'smooth') });
      },
      // Before first paint: the viewport-height and header-height custom
      // properties every section is sized with. Setting them from the loop
      // (after paint) shifted the whole page by the difference between the
      // CSS default and the real frame — CLS 0.03–0.11 on deep links.
      prime() {
        const app = E.app(); if (!app) return;
        const H = app.clientHeight, hd = E.hdH();
        E._vh = H; app.style.setProperty('--vh', H + 'px');
        E._hd = hd; app.style.setProperty('--hd', hd + 'px');
      },
      // Landing pass: one forced frame with .beat transitions disabled, so
      // whatever is in the viewport right now paints in its final state —
      // the first paint after load, or right after a deep-link jump. That
      // is what gives the page a Largest Contentful Paint at all: content
      // that fades in from opacity 0 is never reported as one.
      settle() {
        const app = E.app(); if (!app) return;
        app.dataset.instant = '1';
        E._revealAll = true;
        E._settling = true;
        E.loop(performance.now(), true);
        E._settling = false;
        clearTimeout(E._instantT);
        E._instantT = setTimeout(() => { delete app.dataset.instant; }, 150);
      },
      goSection(id, full, behavior) {
        const app = E.app(); if (!app) return;
        if (id === 's-top') { E.scrollToY(0, behavior); return; }
        const el = id === 'footer' ? app.querySelector('footer') : app.querySelector('#' + id);
        if (!el) return;
        buzz(4);
        const stage = el.hasAttribute('data-stage-sec') || el.hasAttribute('data-open-sec');
        E.scrollToY(el.offsetTop - (full || stage ? 0 : E.hdH()), behavior);
      },
      centerTab(tab) {
        const strip = E.app()?.querySelector('[data-strip]');
        const b = strip && strip.querySelector(`[data-cat="${tab}"]`);
        if (!b) return;
        // Never smooth during the landing pass: a compositor-animated scroll
        // before first paint makes Chrome stop recording Largest Contentful
        // Paint, and the deep-linked page then has no LCP at all.
        strip.scrollTo({ left: Math.max(0, b.offsetLeft - 20), behavior: E.rm || E._settling ? 'auto' : 'smooth' });
      },

      els() {
        if (E._els) return E._els;
        const app = E.app();
        const q = (s) => [...app.querySelectorAll(s)];
        E._els = {
          stages: q('[data-stage-sec]').map((sec) => ({
            sec,
            sticky: sec.querySelector('[data-scene-photo]'),
            photo: sec.querySelector('[data-photo]'),
            img: sec.querySelector('[data-photo] img'),
            title: sec.querySelector('[data-title]'),
            copy: sec.querySelector('[data-copy]'),
          })),
          opens: q('[data-stage-open]').map((el) => ({
            el, sec: el.parentElement, kind: el.dataset.stageOpen,
            table: el.querySelector('[data-table]'),
            frames: el.querySelector('[data-frames]'),
            video: el.querySelector('video'),
          })),
          gl: q('[data-gl-view]'),
          tones: q('[data-tone]'),
          plates: q('[data-plate-img]'),
          reveals: q('[data-reveal]'),
          prog: app.querySelector('[data-prog]'),
          marks: ['s-sig', 's-coffee', 's-food'].map((id) => app.querySelector('#' + id)).filter(Boolean),
          foot: app.querySelector('footer'),
        };
        return E._els;
      },

      loop(now, force) {
        if (!force) {
          if (!E._alive) return;
          requestAnimationFrame(E.loop);
          if (document.hidden) return;
        }
        const app = E.app(), frame = E.frame();
        if (!app || !frame) return;
        const H = app.clientHeight, W = app.clientWidth, st = app.scrollTop;
        const X = E.els();
        E._f++;
        const gl = glRef.current;
        if (gl && (gl.H !== H || gl.W !== W)) gl.resize(W, H);
        const hd = E.hdH();
        const appTop = app.getBoundingClientRect().top;
        const tilt = tiltSrc.tilt.current, tiltT = tiltSrc.tiltT.current;

        // ---- reads ----
        // ph: the pinned photo's own height. It is 86 % of the viewport, not
        // all of it, so the copy panel starts higher than H — everything keyed
        // to where the panel is has to use ph, not H.
        const stages = X.stages.map((S) => ({ S, top: S.sec.offsetTop - st, h: S.sec.offsetHeight, ph: S.sticky ? S.sticky.offsetHeight : H }));
        const opens = X.opens.map((O) => {
          const r = O.el.getBoundingClientRect();
          return { O, top: r.top - appTop, bottom: r.bottom - appTop, secTop: O.sec.offsetTop - st, range: O.sec.offsetHeight - H };
        });
        const glRects = E.detailGL ? [] : X.gl.map((el) => {
          const r = el.getBoundingClientRect();
          return { view: el.dataset.glView, top: r.top - appTop, bottom: r.bottom - appTop };
        });
        const plates = X.plates.map((img) => {
          const r = img.parentElement.getBoundingClientRect();
          return { img, top: r.top - appTop, bottom: r.bottom - appTop };
        });
        const reveals = (E._f % 6 === 0 || E._revealAll)
          ? X.reveals.filter((el) => el.dataset.on !== '1').map((el) => ({ el, r: el.getBoundingClientRect() }))
          : [];
        E._revealAll = false;

        // ---- writes ----
        if (hd !== E._hd) { E._hd = hd; app.style.setProperty('--hd', hd + 'px'); }
        if (H !== E._vh) { E._vh = H; app.style.setProperty('--vh', H + 'px'); }
        tilt.x += (tiltT.x - tilt.x) * 0.06;
        tilt.y += (tiltT.y - tilt.y) * 0.06;
        if (!E.rm) {
          frame.style.setProperty('--tx', (-tilt.x).toFixed(3));
          frame.style.setProperty('--ty', (-tilt.y).toFixed(3));
        }
        for (const { el, r } of reveals) {
          if (r.top - appTop < H * 0.88 && r.bottom - appTop > 0) el.dataset.on = '1';
        }
        for (const { img, top, bottom } of plates) {
          if (img.dataset.src && E.loaded && top < H * 1.5 && bottom > hd + 2) {
            if (img.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
          if (bottom < -60 || top > H + 60) continue;
          const mid = (top + bottom) / 2;
          img.style.transform = E.rm ? 'none' : `translate3d(0, ${(((mid - H / 2) / H) * -34).toFixed(1)}px, 0)`;
        }
        for (const { S, top, h, ph } of stages) {
          // Deferred scene photo: fetch once the scene is within a quarter
          // screen below, or any of it shows past the header from above.
          if (S.img && S.img.dataset.src && E.loaded && top < H * 1.5 && top + h > hd + 2) {
            if (S.img.dataset.srcset) { S.img.srcset = S.img.dataset.srcset; delete S.img.dataset.srcset; }
            S.img.src = S.img.dataset.src;
            delete S.img.dataset.src;
          }
          if (top > H + 40 || top + h < -40) continue;
          const s = clamp(-top, 0, h - ph);
          const e = E.rm ? 1 : smooth(0, 200, s);
          const sd = 24 * e, it = (hd + 14) * e, ib = (s + 14) * e, rad = 6 * e;
          // Scale stays above 1 at every point of the travel — below it the
          // image would be smaller than its frame and show the page behind.
          const dev = E.rm ? 1 : 1 - smooth(0, H * 0.9, top);
          S.photo.style.clipPath = `inset(${it.toFixed(1)}px ${sd.toFixed(1)}px ${ib.toFixed(1)}px ${sd.toFixed(1)}px round ${rad.toFixed(1)}px)`;
          S.img.style.transform = `translate3d(${(-tilt.x * 8).toFixed(1)}px, ${(-tilt.y * 6).toFixed(1)}px, 0) scale(${(1.04 - 0.03 * e + 0.05 * (1 - dev)).toFixed(4)})`;
          S.img.style.filter = dev >= 0.999 ? 'none' : `saturate(${(0.2 + 0.8 * dev).toFixed(3)}) brightness(${(0.55 + 0.45 * dev).toFixed(3)})`;
          const to = E.rm ? (s > 40 ? 0 : 1) : 1 - smooth(0, 180, s);
          S.title.style.opacity = to.toFixed(3);
          S.title.style.transform = `translate3d(0, ${(-30 * (1 - to)).toFixed(1)}px, 0)`;
          const panelTop = top + ph;
          const on = panelTop < H * 0.8 ? '1' : '0';
          if (S.copy.dataset.on !== on) S.copy.dataset.on = on;
          const rail = E.rm ? 1 : clamp((H * 0.82 - panelTop) / (H * 0.5), 0, 1);
          if (S.copy.__rail !== rail) { S.copy.__rail = rail; S.copy.style.setProperty('--rail', rail.toFixed(3)); }
          const tone = panelTop < hd + 30 ? S.sec.dataset.pageTone : 'night';
          if (S.sec.dataset.tone !== tone) S.sec.dataset.tone = tone;
          // 'photo' while the pinned image still owns the viewport, 'panel'
          // once the copy has taken over. The header reads this to decide
          // whether to melt into the page colour or keep the night wash.
          const phase = panelTop < hd + 30 ? 'panel' : 'photo';
          if (S.sec.dataset.phase !== phase) S.sec.dataset.phase = phase;
        }
        for (const { O, top, bottom, secTop, range } of opens) {
          const vis = (Math.min(bottom, H) - Math.max(top, 0)) / H;
          const q = clamp(-secTop / Math.max(1, range), 0, 1);
          // Per-opener state has to outlive els(): those objects are rebuilt on
          // every React commit, and a commit lands right when the opener pins
          // (the tab and header tone both change there). Kept on the objects,
          // "already playing" was forgotten mid-scroll and the pour jumped
          // back to its first frame.
          const OS = E.openState[O.kind] || (E.openState[O.kind] = {});
          if (vis >= 0.5 && O.el.dataset.on !== '1') { O.el.dataset.on = '1'; E.onAt[O.kind] = now; }
          else if (vis <= 0 && O.el.dataset.on === '1') { O.el.dataset.on = '0'; }
          // The twelve plates on the table carry data-src: native lazy loading
          // fetched all of them on every page, because the rotated, perspective
          // grid projects to a bounding box the browser considers in view.
          if (O.kind === 'table' && !OS.tilesOn && E.loaded && top < H * 2 && bottom > hd + 2) {
            OS.tilesOn = true;
            O.el.querySelectorAll('img[data-src]').forEach((im) => { im.src = im.dataset.src; delete im.dataset.src; });
          }
          if (O.kind === 'table' && O.table && vis > 0) {
            const rx = E.rm ? 30 : 62 - 34 * q + tilt.y * 2;
            O.table.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${(tilt.x * 3).toFixed(2)}deg) translate3d(0, ${(-36 * q).toFixed(1)}px, ${(40 * q).toFixed(1)}px)`;
          }
          if (O.kind === 'beans') {
            if (O.video) {
              // The Blender pour: attach the poster a screen ahead, play
              // while any of the section is visible, pause otherwise, and
              // stop at the poster under reduced motion. Only when the
              // opener is below or on screen — landing on the page just
              // after it must not pull 3.7 MB that may never be seen, and
              // Chrome would count a poster peeking under the sticky header
              // as that page's Largest Contentful Paint (it does no
              // occlusion test).
              if (!OS.primed && top < H * 2 && bottom > hd + 2) {
                OS.primed = true;
                if (O.video.dataset.poster) O.video.poster = O.video.dataset.poster;
                O.video.muted = true;
                // No load() here: preload="metadata" on a <source> already
                // selects the resource, and calling load() on a playing
                // element aborts it and rewinds to frame 0.
              }
              const want = vis > 0 && bottom > hd + 2 && !E.rm;
              if (want && O.video.paused && now >= (OS.retryAt || 0)) {
                const p = O.video.play();
                if (p && p.catch) p.catch(() => { OS.retryAt = now + 3000; });
              } else if (!want && !O.video.paused) {
                O.video.pause();
              }
            }
            if (O.frames && vis > 0) {
              const n = Number(O.frames.dataset.count) || 0;
              if (n) {
                const i = 1 + Math.round(q * (n - 1));
                const src = O.frames.dataset.dir + 'f' + String(i).padStart(3, '0') + '.' + (O.frames.dataset.ext || 'webp');
                if (O.frames.getAttribute('src') !== src) O.frames.setAttribute('src', src);
              }
            }
          }
        }
        if (X.prog) {
          const yy = st + hd, M = X.marks;
          let pr = 0;
          for (let i = 0; i < M.length; i++) {
            const a = M[i].offsetTop;
            const b = i + 1 < M.length ? M[i + 1].offsetTop : (X.foot ? X.foot.offsetTop : app.scrollHeight);
            if (yy >= a && yy < b) { pr = (yy - a) / Math.max(1, b - a); break; }
            if (i === M.length - 1 && yy >= b) pr = 1;
          }
          if (pr !== E._pr) { E._pr = pr; X.prog.style.transform = `scaleX(${pr.toFixed(4)})`; }
        }
        if (st !== E._lastSt) { E._lastSt = st; E.scan(st, H, hd, X.tones); }
        if (gl) gl.render(now, glRects, W, H, tilt, E.detailGL);
      },

      // Section background as "r,g,b", or null when it paints nothing.
      // Cached on the node: every section's background comes from a static
      // palette, so this is one style read per section for the whole session.
      bgOf(el) {
        if (el.__bg !== undefined) return el.__bg;
        const m = getComputedStyle(el).backgroundColor.match(/[\d.]+/g) || [];
        const clear = m.length < 3 || (m.length === 4 && parseFloat(m[3]) === 0);
        el.__bg = clear ? null : `${m[0]},${m[1]},${m[2]}`;
        return el.__bg;
      },

      scan(st, H, hd, tones) {
        const y = hd + 1;
        let night = E.night, tab = E.tab, section = 's-top', under = null;
        for (const el of tones) {
          const top = el.offsetTop - st, bot = top + el.offsetHeight;
          if (top <= y && bot > y) { night = el.dataset.tone !== 'day'; under = el; }
          if (top <= y + 2) {
            if (el.id && SECTION_TAB[el.id]) tab = SECTION_TAB[el.id];
            if (el.id) section = el.id; else if (el.tagName === 'FOOTER') section = 'footer';
          }
        }
        // The header takes the colour of the section under it at 88 % — cream,
        // teal, espresso dark, food green — so it melts into each page. A scene
        // whose photo is still pinned keeps the night wash instead.
        let hdBg = null;
        if (under && under.dataset.phase !== 'photo') {
          const rgb = E.bgOf(under);
          if (rgb) hdBg = `rgba(${rgb},.88)`;
        }
        if (night !== E.night || tab !== E.tab || hdBg !== E.hdBg) {
          const tabChanged = tab !== E.tab;
          E.night = night; E.tab = tab; E.hdBg = hdBg;
          onUiRef.current({ night, tab, hdBg });
          if (tabChanged) E.centerTab(tab);
        }
        if (section !== E.section) { E.section = section; E._subs.forEach((cb) => cb(section)); }
      },
    };
    return E;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  engine.rm = rm;
  ref.current = engine;

  // Size vars before first paint, so nothing shifts once the loop starts.
  useLayoutEffect(() => { engine.prime(); }, [engine]);

  useEffect(() => {
    engine._alive = true;
    // Deferred media (neighbouring scene photos, the table's plates) waits for
    // the load event: nothing shares the wire with the first paint's own photo
    // and fonts. (1.5 s after it: the landing's own paint must be done first.)
    // The bean pour is not on this gate: preload="metadata" on its <source>
    // governs the fetch, and the engine only attaches its poster on proximity.
    let t = 0;
    const onLoad = () => { t = window.setTimeout(() => { engine.loaded = true; }, 1500); };
    if (document.readyState === 'complete') onLoad(); else window.addEventListener('load', onLoad, { once: true });
    requestAnimationFrame(engine.loop);
    return () => { engine._alive = false; window.removeEventListener('load', onLoad); window.clearTimeout(t); };
  }, [engine]);

  return engine;
}
